import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import type {AttendanceSummary} from '@/entities/attendance/types';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {formatCount, formatHoursFromMinutes, formatHoursTotal} from '@/shared/lib/format';
import {StatTile} from '@/shared/ui/stat-tile';
import {spacing, useTheme} from '@/shared/ui/theme';
import {formatRate, type IssueFilter, type RosterBreakdown} from '../lib/aggregate';
import {headline} from '../lib/narrative';

/**
 * Шапка отчёта: шесть чисел в ряд, и три из них — кнопки.
 *
 * ## Что здесь было раньше
 *
 * Фраза-вердикт во всю ширину, под ней полоса из трёх процентов, под ней раскрыватель
 * «Что происходило в эти дни» с девятью счётчиками внутри и отдельный блок «Требуют
 * внимания» с пятью фамилиями. Пять этажей, чтобы сообщить то, что в любой системе
 * учёта рабочего времени стоит одной строкой плиток, и таблица — главное, зачем в
 * отчёт заходят, — оказывалась ниже всего этого, да ещё и свёрнутой.
 *
 * ## Почему плитки нажимаются
 *
 * «Пропусков 7088» — число, с которым нечего делать. Вопрос за ним всегда один: КТО
 * именно. Раньше ответ добывался так: доскроллить до таблицы, раскрыть её, найти в её
 * панели отбор, выбрать в нём «кто пропускал». Теперь плитка САМА этот отбор и есть:
 * нажал «Пропусков» — таблица под ней показывает ровно этих людей, а выбранная плитка
 * обведена, чтобы было видно, почему в таблице не все.
 *
 * Три плитки без вопроса «кто» — «Сотрудников», «Явка», «Отработано» — не нажимаются:
 * они описывают всю выборку, и сужать по ним нечего.
 *
 * ## Порядок
 *
 * Слева направо — от масштаба к деталям: сколько людей, как в среднем, что не так,
 * сколько наработали. «Ни разу не отметились» стоит между нарушениями и итогом
 * намеренно: это единственная плитка, считающая не дни, а людей, и почти всегда
 * именно она объясняет, почему явка выглядит катастрофой.
 */
const styles = StyleSheet.create({
  /* Плитки переносятся сами. `flexBasis` 168 — это две штуки в ряд на 390pt телефона
     и шесть на десктопе; фиксированная сетка на четыре колонки оставляла бы на
     телефоне полуторную плитку. */
  row: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2)},
  tile: {flexGrow: 1, flexBasis: 168, minWidth: 150},
});

export function AttendanceKpi({
  summary,
  /**
   * Разложение по людям. Есть у отчёта о НЕСКОЛЬКИХ — компания, отдел; у отчёта по
   * одному человеку его нет, и тогда плитки «Сотрудников» и «Ни разу не отметились»
   * не рисуются: считать людей в отчёте об одном человеке нечего.
   */
  roster,
  /**
   * Текущий отбор таблицы — по нему подсвечивается плитка. Без `onIssue` плитки
   * перестают быть кнопками: сужать нечего, если таблицы под ними нет.
   */
  issue = '',
  onIssue,
}: {
  summary: AttendanceSummary;
  roster?: RosterBreakdown;
  issue?: IssueFilter;
  onIssue?: (next: IssueFilter) => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const {isDesktop} = useBreakpoint();

  const hour = t('attendance.units.hour');
  const minute = t('attendance.units.minute');
  const h = headline(summary);

  /** Повторное нажатие снимает отбор: плитка — переключатель, а не тупик. */
  const toggle = (next: IssueFilter) =>
    onIssue ? () => onIssue(issue === next ? '' : next) : undefined;
  const active = (which: IssueFilter) =>
    onIssue && issue === which ? {borderColor: p.primary, borderWidth: 1} : undefined;

  return (
    <View style={styles.row}>
      {roster ? (
        <StatTile
          testID="kpi-people"
          style={styles.tile}
          compact={!isDesktop}
          icon="people"
          label={t('reports.kpi.people')}
          value={formatCount(roster.total)}
          hint={t('reports.kpi.inScope')}
        />
      ) : null}

      <StatTile
        testID="kpi-attendance"
        style={styles.tile}
        compact={!isDesktop}
        icon="check"
        // Зелёным — только полная явка. Всё остальное красить в «плохо» нельзя: на
        // компании 91% — обычный месяц, и плитка, горящая всегда, не значит ничего.
        tone={h.attendance === 1 ? 'good' : 'neutral'}
        label={t('reports.rate.attendance')}
        value={formatRate(h.attendance)}
        hint={
          h.attendance == null
            ? t('reports.noScheduleShort')
            : t('reports.ofScheduled', {
                present: formatCount(h.presentDays),
                expected: formatCount(h.expected),
              })
        }
        meter={h.attendance == null ? undefined : h.presentDays}
        meterMax={h.attendance == null ? undefined : h.expected}
      />

      <StatTile
        testID="kpi-absent"
        style={[styles.tile, active('absent')]}
        compact={!isDesktop}
        icon="warning"
        tone={summary.absent_days > 0 ? 'bad' : 'neutral'}
        label={t('attendance.summary.absent')}
        value={formatCount(summary.absent_days)}
        hint={
          roster
            ? t('reports.kpi.ofPeople', {count: roster.absent})
            : t('reports.kpi.ofScheduledDays', {count: summary.scheduled_days})
        }
        onPress={toggle('absent')}
      />

      <StatTile
        testID="kpi-late"
        style={[styles.tile, active('late')]}
        compact={!isDesktop}
        icon="clock"
        tone={summary.late_days > 0 ? 'warn' : 'neutral'}
        label={t('attendance.summary.late')}
        value={formatCount(summary.late_days)}
        hint={
          roster
            ? t('reports.kpi.ofPeople', {count: roster.late})
            : t('reports.kpi.ofPresentDays', {count: summary.present_days})
        }
        onPress={toggle('late')}
      />

      {roster ? (
        <StatTile
          testID="kpi-silent"
          style={[styles.tile, active('silent')]}
          compact={!isDesktop}
          icon="personnel"
          // Жёлтым, а не красным, и только когда такие люди есть: это вопрос к данным —
          // терминал, которого нет на проходной, или уволенный, оставшийся в списке, —
          // а не приговор человеку.
          tone={roster.silent > 0 ? 'warn' : 'neutral'}
          label={t('reports.roster.silent')}
          value={formatCount(roster.silent)}
          hint={t('reports.roster.silentHint', {total: formatCount(roster.total)})}
          onPress={toggle('silent')}
        />
      ) : null}

      <StatTile
        testID="kpi-worked"
        style={styles.tile}
        compact={!isDesktop}
        icon="attendance"
        tone="info"
        label={t('attendance.summary.worked')}
        value={formatHoursTotal(summary.total_worked_minutes, hour, minute)}
        // Короткая форма той же подписи: плитка в шестую часть окна режет строку
        // «В среднем 1 ч 7 мин за день присутствия» на «…за ~», то есть ровно на том
        // слове, ради которого её и уточняли.
        hint={
          h.averageMinutes == null
            ? null
            : t('reports.kpi.perDay', {
                value: formatHoursFromMinutes(h.averageMinutes, hour, minute),
              })
        }
      />
    </View>
  );
}
